import { mapGetters } from 'vuex';
import { DEFAULT_PACKAGE_ID } from '@/store/modules/leadType.js';

/**
 * Mixin for components that need to display subsidy information conditionally
 * based on whether the state has percent subsidy enabled
 */
export const subsidyDisplayMixin = {
  computed: {
    ...mapGetters('subsidy', ['isPercentSubsidyEnabledForState']),
    ...mapGetters('leadType', [
      'subsidyToolTipMessage',
      'subsidyByPercentToolTipMessage',
      'selectedPackage',
    ]),
    ...mapGetters('client', ['client']),

    /**
     * Check if subsidy column should be shown
     * Hide for custom packages or non-StateFarm clients
     */
    shouldShowSubsidyColumn() {
      if (this.client.parentCompany !== 'STATEFARM') {
        return false;
      }

      // Hide subsidy column for custom packages
      const packageId = this.selectedPackage?.id;
      const isCustomPackage = !packageId || packageId === DEFAULT_PACKAGE_ID;

      return !isCustomPackage;
    },
  },

  watch: {
    /**
     * Watch for subsidyProduct changes to refresh territory data
     * This ensures territory data is refreshed with the correct subsidyProduct after package changes
     */
    subsidyProduct(newSubsidyProduct, oldSubsidyProduct) {
      if (newSubsidyProduct !== oldSubsidyProduct && this.client.parentCompany === 'STATEFARM') {
        // Refresh territory data when subsidyProduct changes for StateFarm clients
        // This ensures subsidy information reflects the new subsidyProduct
        // Note: The store action updateSubsidyProduct already updates existing territories
        // This watcher provides additional component-specific refresh if needed
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.refreshTerritoryData && typeof this.refreshTerritoryData === 'function') {
              this.refreshTerritoryData();
            }
          }, 100);
        });
      }
    },
  },

  methods: {
    /**
     * Get the display text for subsidy information
     * @param {Object} data - Object containing subsidy data
     * @param {string} data.stateAbbr - State abbreviation
     * @param {number} data.subsidyAmount - Subsidy amount (for non-percent states)
     * @param {number} data.subsidyPercent - Subsidy percentage (for percent states)
     * @param {number} data.subsidyCap - Subsidy cap (for percent states)
     * @returns {string} Formatted subsidy display text
     */
    getSubsidyDisplayText(data) {
      if (!data) return '';

      const stateAbbr = data.stateAbbr || data.stateName;
      if (this.isPercentSubsidyEnabledForState(stateAbbr)) {
        // Display percent and cap for enabled states
        const percent = data.subsidyPercent || 0;
        const cap = data.subsidyCap || 0;
        return `${this.$filters.percent(percent)} (Cap: ${this.$filters.currencyUSD(cap)})`;
      } else {
        // Display amount for non-enabled states
        return this.$filters.currencyUSD(data.subsidyAmount || 0);
      }
    },

    /**
     * Get the tooltip content for subsidy information
     * @param {Object} data - Object containing subsidy data
     * @param {string} data.stateAbbr - State abbreviation
     * @returns {string} Tooltip content
     */
    getSubsidyTooltipContent(data) {
      if (!data) return this.subsidyToolTipMessage;

      const stateAbbr = data.stateAbbr || data.stateName;
      if (this.isPercentSubsidyEnabledForState(stateAbbr)) {
        return this.subsidyByPercentToolTipMessage;
      } else {
        return this.subsidyToolTipMessage;
      }
    },

    /**
     * Check if percent subsidy is enabled for the given data
     * @param {Object} data - Object containing state information
     * @returns {boolean} True if percent subsidy is enabled
     */
    isPercentSubsidyEnabled(data) {
      if (!data) return false;
      const stateAbbr = data.stateAbbr || data.stateName;
      return this.isPercentSubsidyEnabledForState(stateAbbr);
    },
  },
};
